import type { FragmentOf } from "@opensea/graphql"
import { graphql, readFragment } from "@opensea/graphql"
import {
  Flex,
  FlexColumn,
  NumberDisplay,
  Separator,
  SpaceBetween,
  TextBody,
} from "@opensea/ui-kit"
import { useTranslations } from "use-intl"
import { Avatar } from "../Avatar"
import type { WalletDestination } from "../DestinationWalletSelect/types"
import { ViewItemsOnProfileButton } from "../ViewItemsOnProfileButton"
import { TransactionReceiptFragment } from "./TransactionReceipt.fragment"

export const TransactionReceiptCollectionFragment = graphql(`
  fragment TransactionReceiptCollection on Collection {
    imageUrl
  }
`)

type FlowType = "mint" | "purchase" | "acceptOffer"

type TransactionReceiptProps = {
  collection?: FragmentOf<typeof TransactionReceiptCollectionFragment>
  receipt: FragmentOf<typeof TransactionReceiptFragment>
  flowType: FlowType
  walletDestination?: WalletDestination
}

export function TransactionReceipt({
  receipt: receiptKey,
  collection: collectionKey,
  flowType,
  walletDestination,
}: TransactionReceiptProps) {
  const t = useTranslations("wallet.TransactionReceipt")
  const receipt = readFragment(TransactionReceiptFragment, receiptKey)
  const collection = readFragment(
    TransactionReceiptCollectionFragment,
    collectionKey,
  )

  // TODO: Handle multiple currencies
  const totalSpent = receipt.totalSpent?.pricePerToken[0]

  const title = t(`${flowType}.title`)
  const totalLabel = t(`${flowType}.totalLabel`)
  const viewOnProfile = flowType !== "acceptOffer"

  const itemReceiptsQuantity = receipt.itemReceipts.reduce(
    (acc, itemReceipt) => acc + itemReceipt.quantity,
    0,
  )
  const failedItemReceiptsQuantity = receipt.failedItemReceipts.reduce(
    (acc, itemReceipt) => acc + itemReceipt.quantity,
    0,
  )

  return (
    <>
      {receipt.itemReceipts.length !== 0 && (
        <>
          <Separator />
          <FlexColumn className="w-full gap-4">
            <SpaceBetween className="items-center">
              <TextBody size="md" weight="semibold">
                {itemReceiptsQuantity} {title}
              </TextBody>
              {/* TODO: Should we show all here somehow? */}
              <Flex className="gap-4">
                {receipt.itemReceipts.slice(0, 3).map(itemReceipt => (
                  <Avatar
                    frameTime={1}
                    key={itemReceipt.item?.id}
                    size={48}
                    src={
                      itemReceipt.item?.imageUrl ??
                      collection?.imageUrl ??
                      undefined
                    }
                  />
                ))}
              </Flex>
            </SpaceBetween>
            <SpaceBetween>
              <TextBody size="xs" weight="semibold">
                {totalLabel}
              </TextBody>
              <FlexColumn className="justify-end">
                <NumberDisplay
                  suffix={totalSpent?.token.symbol}
                  value={totalSpent?.token.unit}
                />
              </FlexColumn>
            </SpaceBetween>
          </FlexColumn>
        </>
      )}
      {receipt.failedItemReceipts.length !== 0 && (
        <>
          <Separator />
          <SpaceBetween className="w-full items-center">
            <TextBody size="md" weight="semibold">
              {failedItemReceiptsQuantity} {t("failed")}
            </TextBody>
            <Flex className="gap-4 opacity-40">
              {receipt.failedItemReceipts.slice(0, 3).map(itemReceipt => (
                <Avatar
                  frameTime={1}
                  key={itemReceipt.item?.id}
                  size={48}
                  src={
                    itemReceipt.item?.imageUrl ??
                    collection?.imageUrl ??
                    undefined
                  }
                />
              ))}
            </Flex>
          </SpaceBetween>
        </>
      )}
      {receipt.itemReceipts.length !== 0 && viewOnProfile && (
        <>
          <Separator />
          <ViewItemsOnProfileButton
            itemIds={receipt.itemReceipts.flatMap(itemReceipt =>
              itemReceipt.item ? [itemReceipt.item.id] : [],
            )}
            walletDestinationAddress={walletDestination?.address}
          />
        </>
      )}
    </>
  )
}
